import React, { useCallback, useContext, useEffect, useState } from 'react';

import PortfolioContext from '@totem/components/services/optimize/v2/dashboard/PortfolioContext';
import {
  PortfolioOptimizeDashboard,
  ProgramSummary,
} from '@totem/components/services/optimize/v2/dashboard/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  buildingId?: string;
  children: React.ReactNode;
};

const emptyDashboard: PortfolioOptimizeDashboard = {
  totalEnergyCostSavings: 0,
  totalDemandCostSavings: 0,
  totalCostSavings: 0,
  totalKwhSaved: 0,
  totalPeakKwReduced: 0,
  programs: [],
};

const PortfolioContainer = ({ buildingId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [dashboard, setDashboard] =
    useState<PortfolioOptimizeDashboard>(emptyDashboard);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState<ProgramSummary | null>(
    null,
  );

  useEffect(() => {
    if (!refresh) {
      return;
    }

    setRefresh(false);

    setLoading(true);

    const url =
      isNotNull(buildingId) && buildingId !== ''
        ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/dashboard`
        : `${BUILDINGS_ENDPOINT}/optimize/portfolio-dashboard`;

    fetch(url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: PortfolioOptimizeDashboard) => {
        setDashboard(result ?? emptyDashboard);

        setSelectedProgram(null);
      })
      .finally(() => setLoading(false));
  }, [refresh, buildingId, getApiToken]);

  const requestRefresh = useCallback(() => setRefresh(true), []);

  return (
    <PortfolioContext.Provider
      value={{
        buildingId,
        dashboard,
        loading,
        selectedProgram,
        setSelectedProgram,
        requestRefresh,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
};

export default PortfolioContainer;
